import React, { useState, useRef, useEffect } from "react";
import {
    TextField,
    Button,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Box,
} from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import mapboxgl from "mapbox-gl";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import isMock from "../../config";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {useMaterialUIController} from "../../context";

mapboxgl.accessToken =
    "pk.eyJ1IjoibGVvbnRvbWVyIiwiYSI6ImNtNTVjeTdwcTJ3em4yanNkeHg3eDNhaW0ifQ.3Tg0RL9HhK4yQRzVpMMB1Q";

function Places() {
    const [searchTerm, setSearchTerm] = useState("");
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newPlaceName, setNewPlaceName] = useState("");
    const [newPlaceAddress, setNewPlaceAddress] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [controller] = useMaterialUIController();

    const { darkMode } = controller;
    const textColor = darkMode ? "#fff" : "#000";
    const cardBgColor = darkMode ? "#1e293b" : "#fff";
    // New states for Delete Confirmation
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedPlaceToDelete, setSelectedPlaceToDelete] = useState(null);

    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);

    // React Query Client
    const queryClient = useQueryClient();

    // Fetch function for places
    const fetchPlaces = async () => {
        const response = await fetch(
            "http://localhost:3001/places"
        );
        if (!response.ok) {
            throw new Error("Failed to fetch places");
        }
        const data = await response.json();
        // filter so only top-level places are shown
        return isMock ? data.filter((place) => place.parentId === null) : data.places.data.filter((place) => place.parentId === '00000000-0000-0000-0000-000000000000');
    };

    // useQuery to get places data
    const {
        data: places = [],
        isLoading,
        error,
    } = useQuery({
        queryKey: ["places"],
        queryFn: fetchPlaces,
    });

    // useMutation to add a new place
    const addPlaceMutation = useMutation({
        mutationFn: async (newPlaceData) => {
            const response = await fetch(
                "http://localhost:3001/places",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(newPlaceData),
                }
            );
            if (!response.ok) {
                throw new Error("Failed to add new place");
            }
            return response.json();
        },
        onSuccess: () => {
            // Invalidate and refetch the "places" query so the table refreshes
            queryClient.invalidateQueries(["places"]);
        },
    });

    // useMutation to delete a place
    const deletePlaceMutation = useMutation({
        mutationFn: async (id) => {
            const response = await fetch(
                `http://localhost:3001/places/${id}`,
                {
                    method: "DELETE",
                }
            );
            if (!response.ok) {
                throw new Error("Failed to delete place");
            }
            return response.json();
        },
        onSuccess: () => {
            // Refresh places after successful delete
            queryClient.invalidateQueries(["places"]);
        },
    });

    useEffect(() => {
        console.log(places);
    }, [places]);

    const columns = [
        {
            accessorKey: "name",
            header: "Name",
            Cell: ({ row }) => (
                <a
                    href={`/places/${row.original.id}`}
                    style={{ color: darkMode ? "#059be5" : "blue", textDecoration: "underline", cursor: "pointer" }}
                >
                    {row.original.name}
                </a>
            ),
            filterFn: (row, columnId, filterValue) => searchRowRecursively(row, columnId, filterValue),
        },
        {
            accessorKey: "address",
            header: "Address",
            filterFn: (row, columnId, filterValue) => searchRowRecursively(row, columnId, filterValue),
        },
        // NEW ACTION COLUMN FOR DELETION
        {
            id: "delete",
            header: "Delete",
            Cell: ({ row }) => (
                <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleOpenDeleteDialog(row.original.id)}
                >
                    Delete
                </Button>
            ),
        },
    ];

    // Functions for handling the delete dialog
    const handleOpenDeleteDialog = (placeId) => {
        setSelectedPlaceToDelete(placeId);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setSelectedPlaceToDelete(null);
        setDeleteDialogOpen(false);
    };

    const handleConfirmDelete = () => {
        if (selectedPlaceToDelete) {
            deletePlaceMutation.mutate(selectedPlaceToDelete);
        }
        handleCloseDeleteDialog();
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        setNewPlaceName("");
        setNewPlaceAddress("");
        setSuggestions([]);
        setSelectedLocation(null);
    };

    const handleAddPlace = () => {
        if (newPlaceName && newPlaceAddress && selectedLocation) {
            addPlaceMutation.mutate({
                name: newPlaceName,
                address: newPlaceAddress,
                organizationId: "1ff68b39-0336-4f77-8887-f79efb30375c",
                typeOfPlace: 1
            });
        }
        handleDialogClose();
    };

    const theme = createTheme({
        components: {
            MuiTablePagination: {
                styleOverrides: {
                    root: {
                        backgroundColor: darkMode ? "#1e293b" : "#f5f5f5",
                        color: darkMode ? "#fff" : "#000",
                    },
                    selectIcon: {
                        color: darkMode ? "#fff" : "#000",
                    },
                },
            },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        color: darkMode ? "#fff" : "#000",
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        color: darkMode ? "#fff" : "#000",
                    },
                },
            },
        },
    });

    const handleAddressChange = (address) => {
        setNewPlaceAddress(address);
        fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
                address
            )}.json?access_token=${mapboxgl.accessToken}`
        )
            .then((res) => res.json())
            .then((data) => {
                if (data.features) {
                    setSuggestions(
                        data.features.map((feature) => ({
                            text: feature.place_name,
                            coordinates: feature.geometry.coordinates,
                        }))
                    );
                }
            })
            .catch((error) => console.error("Geocoding error:", error));
    };

    const handleSuggestionClick = (suggestion) => {
        setNewPlaceAddress(suggestion.text);
        setSelectedLocation({
            longitude: suggestion.coordinates[0],
            latitude: suggestion.coordinates[1],
        });
        setSuggestions([]);

        if (mapRef.current) {
            mapRef.current.flyTo({ center: suggestion.coordinates, zoom: 14 });
            new mapboxgl.Marker().setLngLat(suggestion.coordinates).addTo(mapRef.current);
        }
    };

    const handleDialogEntered = () => {
        if (!mapRef.current && mapContainerRef.current) {
            mapRef.current = new mapboxgl.Map({
                container: mapContainerRef.current,
                style: "mapbox://styles/mapbox/streets-v11",
                center: [0, 0],
                zoom: 2,
            });
        } else {
            mapRef.current?.resize();
        }
    };

    const handleDialogExited = () => {
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
        }
    };

    const searchRowRecursively = (row, columnId, filterValue) => {
        const value = row.getValue(columnId);
        if (value?.toLowerCase().includes(filterValue.toLowerCase())) {
            return true;
        }
        if (row.subPlaces?.length) {
            return row.subPlaces.some((subRow) => searchRowRecursively(subRow, columnId, filterValue));
        }
        return false;
    };

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox pt={6} pb={3} px={2}>
                {/* Header with Add Button */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <MDTypography variant="h4">Places</MDTypography>
                    <Button
                        variant="contained"
                        onClick={() => setIsDialogOpen(true)}
                        sx={{
                            backgroundColor: "#1976d2",
                            color: "#fff",
                            "&:hover": { backgroundColor: "#115293" },
                        }}
                    >
                        Add Place
                    </Button>
                </Box>

                {/* MaterialReactTable */}
                <ThemeProvider theme={theme}>
                    <MaterialReactTable
                        columns={columns}
                        data={places}
                        enableExpanding
                        getSubRows={(row) => row?.subPlaces}
                        enableGlobalFilter
                        muiBottomToolbarProps={{
                            sx: {
                                backgroundColor: darkMode ? '#1e293b' : '#fff',
                                color: darkMode ? '#fff' : '#1e293b',
                                ".MuiFormLabel-root": {
                                    color: darkMode ? '#fff' : '#1e293b',
                                },
                                ".MuiSelect-icon": {
                                    color: darkMode ? '#fff' : '#1e293b',
                                },
                                ".MuiSelect-select": {
                                    color: darkMode ? '#fff' : '#1e293b',
                                },
                                ".MuiBox-root.MuiButtonBase-root" : {
                                    color: darkMode ? '#fff' : '#1e293b',
                                },
                                ".MuiSvgIcon-root" : {
                                    color: darkMode ? '#fff' : '#1e293b',
                                },
                                '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
                                    // pick a dimmer color for disabled icons
                                    color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                },
                            }
                        }}
                        muiTopToolbarProps={{
                            sx: {
                                backgroundColor: darkMode ? '#1e293b' : '#fff',
                                color: '#fff',
                                ".MuiSvgIcon-root" : {

                                    color: darkMode ? '#fff' : '#1e293b',

                                },
                                ".MuiTableSortLabel-iconDirectionAsc": {
                                    color: darkMode ? '#fff' : '#1e293b',
                                },
                                '& .MuiInputBase-input': {
                                    color: darkMode ? '#fff' : '#1e293b',
                                }
                            },

                        }}
                        muiTableBodyCellProps={{
                            sx: {
                                backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                                color: darkMode ? "#ffffff" : "#000",
                                ".MuiSvgIcon-root" : {
                                    color: darkMode ? "#ffffff" : "#000",
                                }
                            },
                        }}
                        muiTableHeadCellProps={{
                            sx: {
                                backgroundColor: darkMode ? "#1e293b" : "#0098CF",
                                color: "white",
                                fontWeight: "bold",
                                ".MuiSvgIcon-root" : {
                                    color: darkMode ? "#ffffff" : "#000",
                                },
                                // then specifically target the sort label icon:
                                '& .MuiTableSortLabel-icon': {
                                    color: darkMode ? '#fff !important' : '#000 !important',
                                },
                                // likewise for iconDirectionAsc / iconDirectionDesc if needed
                                '& .MuiTableSortLabel-iconDirectionAsc': {
                                    color: darkMode ? '#fff !important' : '#000 !important',
                                },
                                '& .MuiTableSortLabel-iconDirectionDesc': {
                                    color: darkMode ? '#fff !important' : '#000 !important',
                                },
                                '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
                                    // pick a dimmer color for disabled icons
                                    color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                },
                                '& .MuiInputBase-input': {
                                    color: darkMode ? '#fff' : '#1e293b',
                                }
                            },
                        }}
                        globalFilterFn="recursiveSearch"
                        filterFns={{
                            recursiveSearch: (row, columnId, filterValue) => {
                                return searchRowRecursively(row, columnId, filterValue);
                            },
                        }}
                        // Style the top toolbar (the area containing search, column toggles, etc.)
                        muiTableHeadProps={{
                            sx: {
                                display: "table-header-group",
                                backgroundColor: "rgb(0,0,0)",
                                color: "white",
                            },
                        }}
                        initialState={{
                            expanded: false,
                        }}
                        muiTableContainerProps={{
                            sx: {
                                width: "100%",
                                overflowX: "auto",
                                backgroundColor: "#15aff4",
                                color: "white",
                            },
                        }}

                    />
                </ThemeProvider>
            </MDBox>

            {/* Dialog for Adding a New Place */}
            <Dialog
                open={isDialogOpen}
                onClose={handleDialogClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: cardBgColor,
                        color: textColor, // so text inherits this color by default
                    },
                }}
                TransitionProps={{
                    onEntered: handleDialogEntered,
                    onExited: handleDialogExited,
                }}
            >
                <DialogTitle style={{ color: textColor }}>Add New Place</DialogTitle>
                <DialogContent sx={{ display: "flex", gap: 2 }}>
                    <div style={{ flex: 1 }}>
                        <TextField
                            fullWidth
                            label="Place Name"
                            variant="outlined"
                            value={newPlaceName}
                            onChange={(e) => setNewPlaceName(e.target.value)}
                            sx={{
                                mb: 2,
                                mt: 2,
                                "& .MuiInputLabel-root": {
                                    color: textColor,
                                },
                                "& .MuiInputBase-input": {
                                    color: textColor,
                                },
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Place Address"
                            variant="outlined"
                            sx={{
                                "& .MuiInputLabel-root": {
                                    color: textColor,
                                },
                                "& .MuiInputBase-input": {
                                    color: textColor,
                                },
                            }}
                            value={newPlaceAddress}
                            onChange={(e) => handleAddressChange(e.target.value)}
                        />

                        <List>
                            {suggestions.map((suggestion, index) => (
                                <ListItem button key={index} onClick={() => handleSuggestionClick(suggestion)}>
                                    <ListItemText style={{ color: textColor }} primary={suggestion.text} />
                                </ListItem>
                            ))}
                        </List>
                    </div>

                    <div
                        key={isDialogOpen ? "map-open" : "map-closed"}
                        style={{ flex: 1, height: "400px", marginTop: 16 }}
                        ref={mapContainerRef}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddPlace}
                        color="primary"
                        disabled={!newPlaceName || !newPlaceAddress}
                    >
                        Add Place
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog for Deletion Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
                <DialogTitle sx={{color: textColor}}>Delete Confirmation</DialogTitle>
                <DialogContent sx={{color: textColor}}>
                    Are you sure you want to delete this place?
                </DialogContent>
                <DialogActions sx={{display: "flex", justifyContent: "flex-start"}}>
                    <Button onClick={handleCloseDeleteDialog} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="primary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
}

export default Places;
